import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import type {
  BackgroundColorProps,
  BorderColorProps,
  BorderLeftProps,
  BorderProps,
  BorderRightProps,
} from 'styled-system';
import {
  backgroundColor,
  border,
  borderColor,
  borderLeft,
  borderRight,
  color,
  space,
  width,
} from 'styled-system';

import { Button } from 'common/components/Button';
import ButtonWithIcon from 'common/components/Button/ButtonWithIcon';
import CountDown from 'common/components/CountDown';
import type { FlexProps } from 'common/components/Flex';
import { Flex } from 'common/components/Flex';
import type { CommonTypographyProps } from 'common/components/Typography';
import { BodyRegularBR2, HeadlineH7, LinkRegularLR2 } from 'common/components/Typography';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { BoxSizingProps, TextDecorationProps } from 'common/theme/system-utilities';
import { boxSizing, textDecoration } from 'common/theme/system-utilities';
import { getWithAutoLogoutBanner, performLogout, saveWithAutoLogoutBanner } from '../utils';

interface AutoLogoutBannerProps {
  isShown?: boolean;
}

type FlexContainerProps = FlexProps &
  BackgroundColorProps &
  BorderProps &
  BorderColorProps &
  BorderLeftProps &
  BorderRightProps &
  BoxSizingProps;

const INITIAL_MINUTES_LEFT = 2;
const MAX_MINUTES_TO_ADD = 5;
const MIN_MINUTES_TO_ADD = 1;
const LOGOUT_TIMER_DELAY = 1000;
const COLOR_SECONDARY_TEXT_STRONG = 'sys.secondary.text.strong';

const flexContainerStyle: FlexContainerProps = {
  alignItems: ['center', 'flex-start', 'center'],
  flexDirection: ['column', null, 'row'],
};

const containerStyle: FlexContainerProps = {
  width: '100%',
  border: '1px solid',
  borderLeft: 'none',
  borderRight: 'none',
  borderColor: 'sys.secondary.border.default',
  bg: 'sys.secondary.background.weak',
};

const innerContainerStyle: FlexContainerProps = {
  ...flexContainerStyle,
  justifyContent: 'space-between',
  maxWidth: '1600px',
  width: 1,
  boxSizing: 'border-box',
  py: 'sp_16',
  px: ['sp_16', 'sp_24', 'sp_48', 'sp_64'],
  m: '0 auto',
};

const textFlexContainerStyle: FlexContainerProps = {
  ...flexContainerStyle,
  flexDirection: ['column', null, null, 'row'],
  alignItems: ['flex-start', null, null, 'center'],
};

const ctaFlexContainerStyle: FlexContainerProps = {
  width: [1, null, 'auto'],
  flexDirection: ['column', 'row'],
  alignItems: 'center',
  mt: ['sp_16', null, 'sp_0'],
};

const textStyle: CommonTypographyProps = {
  color: COLOR_SECONDARY_TEXT_STRONG,
  mr: 'sp_8',
};

const titleStyle: CommonTypographyProps = {
  color: COLOR_SECONDARY_TEXT_STRONG,
  mr: ['sp_0', null, null, 'sp_16'],
  mb: ['sp_8', null, null, 'sp_0'],
};

const linkButtonStyle = {
  color: COLOR_SECONDARY_TEXT_STRONG,
  textDecoration: 'underline',
  ml: ['sp_0', null, null, 'sp_16'],
  pl: 'sp_32',
  pr: ['sp_32', 'sp_0'],
};

const buttonWithIconStyle = {
  width: [1, 'auto'],
  mb: ['sp_16', 'sp_0'],
  color: 'sys.neutral.text.default',
};

const Container = styled(Flex)<FlexContainerProps>`
  ${backgroundColor};
  ${border};
  ${borderColor};
  ${borderLeft};
  ${borderRight};
`;

const InnerContainer = styled(Flex)<FlexContainerProps>`
  ${boxSizing};
`;

const StyledLinkButton = styled(Button)<TextDecorationProps>`
  ${color};
  ${space};
  ${textDecoration};
`;

const StyledCountDown = styled(CountDown)`
  & > div {
    margin-left: 0;
    padding-top: 0;
  }
`;

const StyledButtonWithIcon = styled(ButtonWithIcon)`
  ${width};
  ${color};

  & svg {
    stroke: none;
  }
`;

const AutoLogoutBanner = ({ isShown = false }: AutoLogoutBannerProps) => {
  const { t, prefix } = useLocalization();
  const showBanner = isShown || getWithAutoLogoutBanner();
  const redirectUrl = prefix('/');
  const [minutesAdded, setMinutesAdded] = useState(0);
  const [timeLeft, setTimeLeft] = useState(dayjs().add(INITIAL_MINUTES_LEFT, 'minute'));

  useEffect(() => {
    if (isShown && !getWithAutoLogoutBanner()) {
      saveWithAutoLogoutBanner();
    }
  }, [isShown]);

  useEffect(() => {
    if (showBanner) {
      const logoutTimer = setInterval(() => {
        const currentTime = dayjs();

        if (timeLeft.isBefore(currentTime)) {
          performLogout(redirectUrl);
          clearInterval(logoutTimer);
        }
      }, LOGOUT_TIMER_DELAY);

      return () => clearInterval(logoutTimer);
    }
  }, [showBanner, timeLeft]);

  const handleExtendTimeLeftButtonClick = () => {
    setMinutesAdded(minutesAdded + 1);
    setTimeLeft(timeLeft.add(MIN_MINUTES_TO_ADD, 'minute'));
  };

  const handleLogoutNowButtonClick = () => {
    performLogout(redirectUrl);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <Container data-corgi-id="auto-logout-banner" {...containerStyle}>
      <InnerContainer {...innerContainerStyle}>
        <Flex {...textFlexContainerStyle}>
          <HeadlineH7 {...titleStyle}>{t('h24_auto_logout_banner_title')}</HeadlineH7>
          <Flex flexWrap={['wrap', 'nowrap']}>
            <BodyRegularBR2 {...textStyle}>{t('h24_auto_logout_banner_subtitle')}</BodyRegularBR2>
            <StyledCountDown
              color={COLOR_SECONDARY_TEXT_STRONG}
              endDate={dayjs(timeLeft).toJSON()}
            />
          </Flex>
        </Flex>
        <Flex {...ctaFlexContainerStyle}>
          <StyledButtonWithIcon
            {...buttonWithIconStyle}
            iconName="plusStrong"
            onClick={handleExtendTimeLeftButtonClick}
            disabled={minutesAdded === MAX_MINUTES_TO_ADD}
          >
            <LinkRegularLR2 color="inherit">{t('h24_extend_logout_timer')}</LinkRegularLR2>
          </StyledButtonWithIcon>
          <StyledLinkButton {...linkButtonStyle} kind="bare" onClick={handleLogoutNowButtonClick}>
            <LinkRegularLR2>{t('h24_logout_now')}</LinkRegularLR2>
          </StyledLinkButton>
        </Flex>
      </InnerContainer>
    </Container>
  );
};

export default AutoLogoutBanner;
